import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useAuthStore} from '../../store/authStore';
import {ClickTracker, getClickTracker} from '../../utils/firebase';
import {logger} from '../../utils/logger';

interface ClickTrackingContextType {
  clickTracker: ClickTracker | null;
  isInitialized: boolean;
  isEnabled: boolean;
  enableTracking: () => void;
  disableTracking: () => void;
  trackClick: (element: string, metadata?: Record<string, any>) => void;
  trackExerciseSettings: (exerciseType: string, settings: Record<string, any>) => void;
}

export const ClickTrackingContext = React.createContext<ClickTrackingContextType>({
  clickTracker: null,
  isInitialized: false,
  isEnabled: true,
  enableTracking: () => {},
  disableTracking: () => {},
  trackClick: () => {},
  trackExerciseSettings: () => {},
});

export const ClickTrackingProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [clickTracker, setClickTracker] = useState<ClickTracker | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const location = useLocation();
  const {user} = useAuthStore();

  // Initialize click tracker when user is available
  useEffect(() => {
    if (user?.uid && !clickTracker) {
      initializeTracker();
    }
  }, [user]);

  // Track page navigation
  useEffect(() => {
    if (isInitialized && clickTracker && user?.uid) {
      trackPageView();
    }
  }, [location.pathname, isInitialized]);

  const initializeTracker = async () => {
    try {
      if (!user?.uid) return;

      logger.info('Initializing click tracker...');

      try {
        const tracker = await getClickTracker(user.uid);
        setClickTracker(tracker);
        setIsInitialized(true);
        logger.info('Click tracker initialized successfully');
      } catch (error) {
        // Continue without tracking if we hit permission issues
        logger.error('Error initializing click tracker, continuing without analytics:', error);
        // Create a dummy tracker that doesn't do anything to prevent app crashes
        setClickTracker({
          disable: () => {},
          enable: () => {},
          trackClick: () => {},
          trackExerciseSettings: () => {},
        });
        setIsInitialized(true);
      }
    } catch (error) {
      logger.error('Failed to initialize click tracker:', error);
      // Ensure app doesn't crash by setting a dummy tracker
      setClickTracker({
        disable: () => {},
        enable: () => {},
        trackClick: () => {},
        trackExerciseSettings: () => {},
      });
      setIsInitialized(true);
    }
  };

  const trackPageView = () => {
    if (!clickTracker || !isEnabled) return;

    try {
      clickTracker.trackClick('page_view', {
        pathname: location.pathname,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Error tracking page view:', error);
    }
  };

  const enableTracking = () => {
    if (clickTracker) {
      try {
        clickTracker.enable();
      } catch (error) {
        logger.error('Error enabling tracking:', error);
      }
    }
    setIsEnabled(true);
  };

  const disableTracking = () => {
    if (clickTracker) {
      try {
        clickTracker.disable();
      } catch (error) {
        logger.error('Error disabling tracking:', error);
      }
    }
    setIsEnabled(false);
  };

  const trackClick = (element: string, metadata?: Record<string, any>) => {
    if (clickTracker && isEnabled) {
      try {
        clickTracker.trackClick(element, metadata);
      } catch (error) {
        logger.error('Error tracking click:', error);
      }
    }
  };

  const trackExerciseSettings = (exerciseType: string, settings: Record<string, any>) => {
    if (clickTracker && isEnabled) {
      try {
        clickTracker.trackExerciseSettings(exerciseType, settings);
      } catch (error) {
        logger.error('Error tracking exercise settings:', error);
      }
    }
  };

  return (
    <ClickTrackingContext.Provider
      value={{
        clickTracker,
        isInitialized,
        isEnabled,
        enableTracking,
        disableTracking,
        trackClick,
        trackExerciseSettings,
      }}
    >
      {children}
    </ClickTrackingContext.Provider>
  );
};

// Custom hook to use click tracking
export const useClickTracking = () => React.useContext(ClickTrackingContext);
